import React, { useCallback, useEffect, useState, useRef } from 'react';
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
const NAMES = ['María', 'Carmen', 'Ana', 'Laura', 'Isabel', 'Sofía', 'Marta', 'Lucía', 'Elena', 'Paula', 'José', 'Antonio', 'Manuel', 'Francisco', 'David', 'Juan', 'Javier', 'Daniel', 'Carlos', 'Alejandro', 'Jessica', 'Ashley', 'Amanda', 'Sarah', 'Jennifer', 'Michael', 'Christopher', 'Matthew', 'Joshua', 'Andrew', 'Valentina', 'Camila', 'Valeria', 'Mariana', 'Gabriela', 'Santiago', 'Mateo', 'Sebastián', 'Leonardo', 'Diego'];
const CITIES = ['Miami, FL', 'Los Angeles, CA', 'New York, NY', 'Houston, TX', 'Chicago, IL', 'Phoenix, AZ', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC', 'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Las Vegas, NV'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'Freelance Desde Cero',
  editionLabel: 'Guía + 6 Bonos',
  priceNumber: 14,
  priceText: '$14 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/tj4w3dzjdE8gLhXHBdBFWk/BONO_1.png'
}, {
  title: 'Freelance Desde Cero',
  editionLabel: 'Guía + Scripts de Poder',
  priceNumber: 23,
  priceText: '$23 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/tiRmjyzmVTm5Ais22gdmbP/BONO_2.png'
}, {
  title: 'Freelance Desde Cero',
  editionLabel: 'Guía + Contrato Relámpago',
  priceNumber: 23,
  priceText: '$23 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/tj4w3dzjdE8gLhXHBdBFWk/BONO_1.png'
}, {
  title: 'Freelance Desde Cero',
  editionLabel: 'Pack Completo',
  priceNumber: 32,
  priceText: '$32 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/tiRmjyzmVTm5Ais22gdmbP/BONO_2.png'
}];
export function RecentPurchaseToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [currentToast, setCurrentToast] = useState<{
    name: string;
    city: string;
    product: ScannedProduct;
  } | null>(null);
  const productsRef = useRef<ScannedProduct[]>([]);
  const recentNamesRef = useRef<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const prefersReducedMotion = useRef(false);
  // Use fixed products with correct prices (DOM scanner disabled to avoid picking up crossed-out prices)
  const scanProducts = useCallback(() => {
    productsRef.current = FALLBACK_PRODUCTS;
  }, []);
  const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
  const getNextName = useCallback(() => {
    let availableNames = NAMES.filter(n => !recentNamesRef.current.includes(n));
    if (availableNames.length === 0) {
      availableNames = NAMES;
      recentNamesRef.current = [];
    }
    const name = getRandomItem(availableNames);
    recentNamesRef.current = [...recentNamesRef.current.slice(-9), name];
    return name;
  }, []);
  const getNextProduct = useCallback(() => {
    // Weighted: 50% base $14, 20% +scripts $23, 15% +contrato $23, 15% pack $32
    const r = Math.random();
    if (r < 0.5) return FALLBACK_PRODUCTS[0];
    if (r < 0.7) return FALLBACK_PRODUCTS[1];
    if (r < 0.85) return FALLBACK_PRODUCTS[2];
    return FALLBACK_PRODUCTS[3];
  }, []);
  const showNextToast = useCallback(() => {
    setCurrentToast({
      name: getNextName(),
      city: getRandomItem(CITIES),
      product: getNextProduct()
    });
    setIsVisible(true);
    setIsAnimatingOut(false);
    // Hide after 4 seconds
    timeoutRef.current = setTimeout(() => {
      setIsAnimatingOut(true);
      // Remove from DOM after animation completes
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        // Schedule next toast (8-15 seconds)
        const nextDelay = Math.floor(Math.random() * (15000 - 8000 + 1) + 8000);
        timeoutRef.current = setTimeout(showNextToast, nextDelay);
      }, prefersReducedMotion.current ? 0 : 300);
    }, 4000);
  }, [getNextName, getNextProduct]);
  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scanProducts();
    // Initial delay of 3 seconds
    timeoutRef.current = setTimeout(showNextToast, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [scanProducts, showNextToast]);
  if (!isVisible || !currentToast) return null;
  const animationClass = prefersReducedMotion.current ? '' : isAnimatingOut ? 'animate-toast-slide-down' : 'animate-toast-slide-up';
  return <>
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes toast-slide-up {
          from { transform: translateY(100%) translateX(-50%); opacity: 0; }
          to { transform: translateY(0) translateX(-50%); opacity: 1; }
        }
        @keyframes toast-slide-down {
          from { transform: translateY(0) translateX(-50%); opacity: 1; }
          to { transform: translateY(100%) translateX(-50%); opacity: 0; }
        }
        .animate-toast-slide-up {
          animation: toast-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .animate-toast-slide-down {
          animation: toast-slide-down 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `
    }} data-id="element-184" />

      <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-[380px] bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-[#b8956a]/20 p-3 flex items-center gap-3 ${animationClass}`} role="status" aria-live="polite" data-id="element-185">
        <div className="relative flex-shrink-0" data-id="element-186">
          <img src={currentToast.product.thumbnailUrl} alt={currentToast.product.title} className="w-10 h-10 rounded-lg object-cover border border-[#f5e6e0]" data-id="element-187" />
          <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#7a9a6b] rounded-full border-2 border-white flex items-center justify-center" data-id="element-188">
            <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-189">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-190" />
            </svg>
          </div>
        </div>

        <div className="flex-1 min-w-0" data-id="element-191">
          <p className="text-sm text-[#3d2b1f] leading-tight truncate" data-id="element-192">
            <span className="font-bold" data-id="element-193">{currentToast.name}</span> de{' '}
            {currentToast.city}
          </p>
          <p className="text-xs text-[#6b5744] mt-0.5 leading-tight truncate" data-id="element-194">
            compró{' '}
            <span className="font-medium text-[#3d2b1f]" data-id="element-195">
              {currentToast.product.editionLabel}
            </span>
          </p>
          <p className="text-xs font-bold text-[#b8956a] mt-0.5" data-id="element-196">
            {currentToast.product.priceText}
          </p>
        </div>
      </div>
    </>;
}